<template>
    <div class="user-management">
        <div class="user-management-header">
            <div class="title">
                {{$t('user.management.title')}}
            </div>
            <v-btn @click="toggleShowSendInvitePopup" color="primary" class="invite-users-btn">
                {{$t('user.management.btn.invite.users')}}
            </v-btn>
        </div>
        <div class="user-management-table" v-if="formatItems.length > 0">
          <div class="user-management-table__header">
            {{$t('user.management.table.title')}}
          </div>
            <wombat-table
                :items="formatItems"
                :columns="tableColumns"
                :loadingItems="false"
                :resize="true"
                :columnsReorder="false"
                :infiniteLoading="false"
                :itemHeight="58"
                :noResultsMessage="$t('noResultsFound')"
                name='signedUsersTable'
            >
                <component
                    slot="row-cell"
                    slot-scope="rowCell"
                    class="row-cell"
                    :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
                    :item="rowCell.item"
                    :column="rowCell.column"
                    @deleteUser='openDeleteUserPopup'
                    @editUser="openEditUserPopup"
                    @resetPassword="openResetPasswordPopup"
                    @disableDeleteButton="handleDisableDeleteButton"
                    @handleChangeUserRoleError="handleChangeUserRoleError"
                ></component>
            ></wombat-table>
        </div>
      <div class="pending-user-management-table" v-if="formatPendingUsers.length > 0">
        <div class="pending-user-management-table__header">
          {{$t('pending.user.management.table.title')}}
        </div>
        <wombat-table
          :items="formatPendingUsers"
          :columns="pendingTableColumns"
          :loadingItems="false"
          :resize="true"
          :columnsReorder="false"
          :infiniteLoading="false"
          :itemHeight="58"
          :noResultsMessage="$t('noResultsFound')"
          name='pendingUsersTable'
        >
          <component
            slot="row-cell"
            slot-scope="rowCell"
            class="row-cell"
            :is="pendingTableRowCellComponentsHash[rowCell.column.name] || pendingTableRowCellComponentsHash.default"
            :item="rowCell.item"
            :column="rowCell.column"
            @deleteUser='openDeletePendingUserPopup'
            @resendInvite="openResendInvitationPopup"
          ></component>
          ></wombat-table>
      </div>

      <send-invitation-popup
        :visiblePopup="showSendInvitePopup"
        @closeInvitePopup="toggleShowSendInvitePopup"
        @saveInvite="sendInvitations"
      >
      </send-invitation-popup>
      <delete-user-popup
        :visible-popup='displayDeleteUserPopup'
        title="delete.user.popup.title"
        :message="userAccess ? 'delete.user.popup.access.message' : 'delete.user.popup.message'"
        :showCancelButton='!userAccess'
        @clicked='deleteUserPopupClicked'
      />
      <delete-user-popup
        :visible-popup='displayDeletePendingUserPopup'
        title="delete.pending.user.popup.title"
        message="delete.pending.user.popup.message"
        @clicked='deletePendingUserPopupClicked'
      />
      <success-operation-message
        :message='successMessage'
        @close="clearSuccessMessage"
      />
      <error-message :errorText="errorMessage" @close="errorMessage = ''" />
      <edit-user-details-popup
        :visible-popup='showEditUserDetailsPopup'
        @closed='showEditUserDetailsPopup = false'
        @save='saveUser'
      />
      <new-password-popup
        :visible-popup="showNewPasswordPopup"
        @clicked="closePasswordPopup"
        />
      <yes-no-popup
        :visible-popup="showConfirmResetPasswordPopup"
        :title="'reset.user.password.popup.title'"
        :message="'reset.user.password.popup.message'"
        :no-caption="'confirm.cancel'"
        :yes-caption="'confirm.ok'"
        :loading="loadingForResetPasswordPopup"
        @clicked="confirmResetPasswordPopupClicked"
      />
      <yes-no-popup
        :visible-popup="displayResendInvitationPopup"
        :title="'resend.user.invitation.popup.title'"
        :message="'resend.user.invitation.popup.message'"
        :no-caption="'confirm.cancel'"
        :yes-caption="'confirm.ok'"
        :loading="loadingForResendInvitationPopup"
        @clicked="confirmResendInvitationPopupClicked"
      />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {getEmptyColumn, pendingUserManagementTableColumns, userManagementTableColumns} from '@/services/tableColumns';
import {
  ADMIN_INVITE_USERS,
  DISABLE_DELETE_USER_BUTTON,
  LOAD_ALL_TENANT_USERS,
  SAVE_USER, RESET_USER_PASSWORD,
  CLEAR_RESET_USER_PASSWORD,
  DELETE_USER, DELETE_PENDING_USER, SET_USER_DETAILS_FOR_EDIT,
} from '@/store/user/actionTypes';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import NameTableCell from '@/components/tableCells/userManagementTableCells/NameTableCell.vue';
import EmailTableCell from '@/components/tableCells/userManagementTableCells/EmailTableCell.vue';
import RoleTableCell from '@/components/tableCells/userManagementTableCells/RoleTableCell.vue';
import ActionTableCell from '@/components/tableCells/userManagementTableCells/ActionTableCell.vue';
import CreationDateTableCell from '@/components/tableCells/userManagementTableCells/CreationDateTableCell.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import SendInvitationPopup from '@/components/popup/SendInvitaionPopup.vue';
import TextField from '@/components/common/TextField.vue';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import DeleteUserPopup from '@/components/popup/DeleteUserPopup.vue';
import {CONFIGURATION_ACTION_TYPES} from '@/constants/configuration';
import SuccessOperationMessage from '@/components/common/SuccessOperationMessage.vue';
import {RESET_SUCCESS_OPERATION_MESSAGE} from '@/store/configuration/actionTypes';
import EditUserDetailsPopup from '@/components/popup/EditUserDetailsPopup.vue';
import NewPasswordPopup from '@/components/popup/NewPasswordPopup.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';

export default {
  name: 'UserManagement',
  components: {
    TextField,
    WombatTable,
    SendInvitationPopup,
    CustomCheckbox,
    DeleteUserPopup,
    SuccessOperationMessage,
    EditUserDetailsPopup,
    NewPasswordPopup,
    YesNoPopup,
    ErrorMessage,
  },
  data() {
    return {
      showSendInvitePopup: false,
      displayDeleteUserPopup: false,
      userObjectToDelete: null,
      userAccess: false,
      disableDeleteUserButton: false,
      displayDeletePendingUserPopup: false,
      displayResendInvitationPopup: false,
      pendingUserObjectToDelete: null,
      userEmailToResendInvitation: null,
      successMessage: '',
      errorMessage: '',
      showEditUserDetailsPopup: false,
      showNewPasswordPopup: false,
      userObjectToReset: null,
      showConfirmResetPasswordPopup: false,
      loadingForResetPasswordPopup: false,
      loadingForResendInvitationPopup: false,
    };
  },
  async created() {
    await this.$store.dispatch(LOAD_ALL_TENANT_USERS);
  },
  watch: {
    successOperationMessage() {
      this.successMessage = this.successOperationMessage;
    },
  },
  computed: {
    ...mapGetters(['tenantUsers', 'profile', 'pendingUsers', 'successOperationMessage', 'companyData']),
    formatItems() {
      return this.tenantUsers.map(user => ({
        id: user.id,
        userName: user.name,
        email: user.email,
        phoneNumber: user.phone,
        access: user.access,
        role: user.role, // Pass the role array directly, not mapped
      }));
    },
    formatPendingUsers() {
      return [...this.pendingUsers].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    tableColumns() {
      const columns = [
        userManagementTableColumns.userName,
        userManagementTableColumns.email,
        userManagementTableColumns.phoneNumber,
        userManagementTableColumns.role,
        userManagementTableColumns.action,
      ];
      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    tableRowCellComponentsHash() {
      return {
        userName: NameTableCell,
        email: EmailTableCell,
        phoneNumber: WombatRowDefaultCell,
        role: RoleTableCell,
        action: ActionTableCell,
        default: WombatRowDefaultCell,
      };
    },
    pendingTableColumns() {
      const columns = [
        pendingUserManagementTableColumns.email,
        pendingUserManagementTableColumns.date,
        pendingUserManagementTableColumns.action,
      ];
      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    pendingTableRowCellComponentsHash() {
      return {
        email: EmailTableCell,
        date: CreationDateTableCell,
        action: ActionTableCell,
        default: WombatRowDefaultCell,
      };
    },
  },
  methods: {
    async saveUser(userData) {
      try {
        await this.$store.dispatch(SAVE_USER, userData);
        this.showEditUserDetailsPopup = false;
      } catch (e) {
        this.showEditUserDetailsPopup = false;
        this.errorMessage = e.message;
      }
    },
    async openEditUserPopup(user) {
      await this.$store.dispatch(SET_USER_DETAILS_FOR_EDIT, user);
      this.showEditUserDetailsPopup = true;
    },
    async openResetPasswordPopup(userObject) {
      this.userObjectToReset = userObject;
      this.showConfirmResetPasswordPopup = true;
    },
    async confirmResetPasswordPopupClicked(value) {
      if (value) {
        this.loadingForResetPasswordPopup = true;
        await this.resetUserPassword();
        this.loadingForResetPasswordPopup = false;
      }
      this.showConfirmResetPasswordPopup = false;
      this.userObjectToReset = null;
    },
    async resetUserPassword() {
      try {
        await this.$store.dispatch(RESET_USER_PASSWORD, {
          user: this.userObjectToReset,
          successMessageType: CONFIGURATION_ACTION_TYPES.RESET_PASSWORD,
        });
        this.showNewPasswordPopup = true;
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async closePasswordPopup() {
      this.showNewPasswordPopup = false;
      await this.$store.dispatch(CLEAR_RESET_USER_PASSWORD);
    },
    async openDeleteUserPopup(userObject) {
      this.userObjectToDelete = userObject;
      this.userAccess = this.userObjectToDelete.access;
      this.displayDeleteUserPopup = true;
    },
    async handleDisableDeleteButton(userId) {
      await this.$store.dispatch(DISABLE_DELETE_USER_BUTTON, { userId });
    },
    async handleChangeUserRoleError(message) {
      this.errorMessage = message;
    },
    async deleteUserAction() {
      try {
        await this.$store.dispatch(DELETE_USER, {
          userId: this.userObjectToDelete.id,
          successMessageType: CONFIGURATION_ACTION_TYPES.DELETE_USER,
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async deleteUserPopupClicked(value) {
      if (value && !this.userAccess) {
        await this.deleteUserAction();
      }
      this.displayDeleteUserPopup = false;
      this.userObjectToDelete = null;
      this.userAccess = false;
    },
    async openDeletePendingUserPopup(pendingUserObject) {
      this.pendingUserObjectToDelete = pendingUserObject;
      this.displayDeletePendingUserPopup = true;
    },
    async deletePendingUserAction() {
      try {
        await this.$store.dispatch(DELETE_PENDING_USER, {
          pendingUserId: this.pendingUserObjectToDelete.id,
          successMessageType: CONFIGURATION_ACTION_TYPES.DELETE_PENDING_USER,
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async deletePendingUserPopupClicked(value) {
      if (value) {
        await this.deletePendingUserAction();
      }
      this.displayDeletePendingUserPopup = false;
      this.pendingUserObjectToDelete = null;
    },
    async openResendInvitationPopup(userEmail) {
      this.userEmailToResendInvitation = userEmail;
      this.displayResendInvitationPopup = true;
    },
    async confirmResendInvitationPopupClicked(value) {
      if (value) {
        this.loadingForResendInvitationPopup = true;
        await this.sendInvitations([{email: this.userEmailToResendInvitation}], true);
        this.loadingForResendInvitationPopup = false;
      }
      this.displayResendInvitationPopup = false;
      this.userEmailToResendInvitation = null;
    },
    toggleShowSendInvitePopup() {
      this.showSendInvitePopup = !this.showSendInvitePopup;
    },
    async sendInvitations(emails, resend = false) {
      if (!resend) {
        this.toggleShowSendInvitePopup();
      }
      const {domain} = this.companyData;
      const companyId = this.profile.company.id;
      // eslint-disable-next-line no-param-reassign
      emails = emails.map(currEmail => currEmail.email);
      try {
        await this.$store.dispatch(ADMIN_INVITE_USERS, {
          domain,
          companyId,
          emails,
          successMessageType: CONFIGURATION_ACTION_TYPES.INVITE_USER,
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async clearSuccessMessage() {
      await this.$store.dispatch(RESET_SUCCESS_OPERATION_MESSAGE);
    },
  },
};
</script>

<style lang="scss">
.user-management {
  .wombat-table {
    --displayactioncell:none;
    padding: 15px 0;
    min-height: auto !important;
    max-width: 100%;
    .ps__rail-x {
      display: none !important;
    }
    .wombat-header{
      padding-bottom: 10px;
      max-height: 40px;
    }
    .wombat-row {
      border-top: none;
      border-left: none;
      border-right: none;
    }
    .wombat-overlay {
      &:hover{
        background-color: var(--sidebar-active-bg);
        --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(244,244,248,0) 78%, var(--sidebar-active-bg) 91%, var(--sidebar-active-bg) 100%);
        --displayactioncell: flex;
      }
    }
  }
}
</style>

<style lang="scss" scoped>

@import "../assets/styles/variables";

.user-management-header{
    flex:0 0 70px;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1%;
    margin-top: 2%;
    .title{
      color: var(--text-primary);
      font-family: $default-application-font-bold;
      font-size: 20px;
      font-weight: bold;
      line-height: 20px;
      letter-spacing: 0;
      margin-bottom: 10px;
    }
}
.invite-users-btn{
    border-radius: 2px;
    color: #FFFFFF;
    box-shadow: 0 1px 1px 0 rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 3px 0 rgba(0,0,0,0.2);
    background-color: $default-button-color !important;
    border-color: $default-button-color !important;
}
.user-management
{
    margin-left: 3%;
    padding: 0px 86px 0px 32px;
    display: flex;
    flex-direction:column;
    overflow-y: auto;
    overflow-x: hidden;
}
.user-management-settings-header {
  margin-bottom: 1%;
  margin-top: 2%;
  &__title, &__subtitle {
    color: var(--text-primary);
    font-family: $default-application-font-bold;
    font-size: 22px;
    font-weight: bold;
    line-height: 20px;
    letter-spacing: 0;
    margin-bottom: 20px;
  }
  &__subtitle {
    font-size: 18px;
    color: var(--text-secondary);
  }
}
.user-management-table, .pending-user-management-table {
  font-family:"SF Pro Display Medium", sans-serif;
  margin-bottom: 20px;
  overflow-x: hidden;
  &__header {
    color: var(--text-secondary);
    font-family: $default-application-font-bold;
    font-size: 18px;
    font-weight: bold;
    line-height: 20px;
    letter-spacing: 0;
    margin-bottom: 20px;
  }
  .wombat-table {
    --displayactioncell:none;
    padding: 15px 0;
    .wombat-header{
      padding-bottom: 10px;
    }
    .cell {
      z-index: 10;
    }
    .wombat-row {
      border-top: none;
      border-left: none;
      border-right: none;
    }
    .wombat-overlay {
      &:hover{
        background-color: var(--sidebar-active-bg);
        --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(244,244,248,0) 78%, var(--sidebar-active-bg) 91%, var(--sidebar-active-bg) 100%);
        --displayactioncell: flex;
      }
    }
  }
}

.user-management-settings-add-email {
  margin-bottom: 2%;
  .text-field-container {
    margin-top: -20px;
    .input-description {
      min-width: 100%;
      justify-self: left;
    }
  }
  &__list {
    .email {
      margin: 10px 5px 5px 0;
      background-color: $tag-chip-background-color;
      font-family: $default-application-font-regular;
    }
  }
  &__title {
    text-align: left;
    color: $input-label-color;
    font-family: $default-application-font-regular !important;
    font-size: 12px !important;
    line-height: 20px;
    letter-spacing: 1px !important;
    text-transform: uppercase;
  }
  &__btn {
    margin-top: 10px;
    .add-email-btn {
      width: 100px;
    }
  }

  &__notify {
    margin-top: 30px;
    &__value {
      display: flex;
      &__text {
        font-family: $default-application-font-regular !important;
        font-size: 14px !important;
        color: $input-text-color;
        margin-top: -2px;
        margin-left: -3px;
      }
    }

    &__message {
      margin-top: 15px;
      &__title {
        font-family: $default-application-font-regular !important;
        font-size: 12px !important;
        color: $input-label-color;
        text-transform: uppercase;
      }
      textarea {
        min-width: 700px;
        min-height: 100px;
        margin-top: 5px;
        border: 1px solid $tag-chip-background-color;
        &:focus {
          outline: none !important;
        }
      }
      &__buttons {
        margin-top: 10px;
        .save {
          margin-right: 20px;
        }
      }
    }
  }
}
</style>
